import React, { useEffect, useMemo, useRef, useState } from "react"
import TableContainer from "components/Common/TableContainer"
import eye from "../../assets/images/icons/eye.svg"
import deleteIcon from "../../assets/images/icons/delete-red.svg"
import { Link, useNavigate } from "react-router-dom"
import {
  useDeleteIcdMutation,
  useLazyGetIcdsListQuery,
} from "store/api/icds/icds.api"
import DeleteConfirmationModal from "modals/deleteConfirmation"
import moment from "moment"
import {
  formatAmount,
  getIcdsStatus,
  getTableSortingHeaderIcon,
} from "helpers/common"
import { usePermission } from "hooks/usePermission"
import { PERMISSIONS } from "helpers/roles"

const defaultFilters = {
  search: "",
  page: 1,
  created_by: "ADMIN",
  pageSize: 20,
  timestamp: "",
}

const MyICDList = () => {
  const navigate = useNavigate()
  const [getResults, { isFetching }] = useLazyGetIcdsListQuery()
  const [onDelete, { isLoading: isDeleting }] = useDeleteIcdMutation()
  const [filters, setFilters] = useState(defaultFilters)
  const [data, setData] = useState([])
  const [count, setCount] = useState(0)
  const [isToast, setIsToast] = useState(false)
  const deleteRef = useRef(null)
  const { hasPermission, hasAnyPermission } = usePermission()

  const getList = async () => {
    try {
      const { data } = await getResults(filters).unwrap()
      setData(data?.rows)
      setCount(data?.count)
    } catch (e) {}
  }

  useEffect(() => {
    getList()
  }, [filters])

  const onDeleteSuccess = () => {
    setIsToast(false)
    deleteRef.current = null
    setFilters((prev) => ({ ...prev, page: 1, timestamp: moment().unix() }))
  }

  const columns = useMemo(() => {
    const data = [
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Request ID"),
        accessorKey: "unique_code",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="code-number">{row?.original.unique_code}</div>
        ),
      },

      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Amount (₹)"),
        accessorKey: "amount",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => <div>₹ {formatAmount(row?.original.amount)}</div>,
      },
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Interest"),
        accessorKey: "interest_rate",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="">{row?.original.interest_rate} %</div>
        ),
      },
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Tenure"),
        accessorKey: "tenure",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => <div className="">{row?.original.tenure} Days</div>,
      },
      {
        header: ({ column }) =>
          getTableSortingHeaderIcon(column, "Date of Repayment"),
        accessorKey: "repayment_date",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="">
            {row?.original.repayment_date
              ? moment(row?.original.repayment_date).format("D MMM YYYY")
              : ""}
          </div>
        ),
      },
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Offers"),
        accessorKey: "totalOffers",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: ({ column }) =>
          getTableSortingHeaderIcon(column, "Request Raised Date"),
        accessorKey: "formatted_created_at",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Status"),
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="code-status">
            {getIcdsStatus(row?.original.status)}
          </div>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <div className="d-flex justify-content-center align-items-center gap-3">
            {hasPermission(PERMISSIONS.ICD_VIEW) && (
              <div className="action-view">
                <Link to={`/icd/offers/${row?.original.id}`}>
                  <img src={eye} alt="" />
                </Link>
              </div>
            )}
            {hasPermission(PERMISSIONS.ICD_DELETE) &&
              row?.original?.totalOffers <= 0 && (
                <div
                  className="action-delete"
                  onClick={() => {
                    deleteRef.current = row?.original
                    setIsToast(true)
                  }}
                >
                  <img src={deleteIcon} alt="" />
                </div>
              )}
          </div>
        ),
      },
    ]

    const canAccess = hasAnyPermission([
      PERMISSIONS.ICD_UPDATE,
      PERMISSIONS.ICD_VIEW,
      PERMISSIONS.ICD_DELETE,
    ])

    if (canAccess) return data
    return data.filter((item) => item.id !== "actions")
  }, [hasAnyPermission])

  const FilterComponents = () => (
    <>
      <Link className="btn btn-secondary" to={"/icd/create"}>
        <i className="mdi mdi-plus me-1"></i> Add Request
      </Link>
    </>
  )

  return (
    <React.Fragment>
      <div className="mb-5">
        <TableContainer
          isLoading={isFetching}
          count={count}
          filters={filters}
          setFilters={setFilters}
          FilterComponents={FilterComponents}
          columns={columns}
          data={data}
          isGlobalFilter={true}
          isCustomPageSize={true}
          isPagination={true}
          pagination="pagination"
          paginationWrapper="dataTables_paginate paging_simple_numbers"
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
        />

        {isToast && deleteRef && (
          <DeleteConfirmationModal
            open={isToast}
            id={deleteRef.current.id}
            setOpen={setIsToast}
            isLoading={isDeleting}
            onClose={() => {
              setIsToast(false)
              deleteRef.current = null
            }}
            title={"Delete ICD"}
            description="Are you sure you want to delete this icd?"
            onDelete={onDelete}
            onSuccess={onDeleteSuccess}
          />
        )}
      </div>
    </React.Fragment>
  )
}

export default MyICDList
